// Data seam for the Agenda area (Doctores + Citas). Each function unwraps the
// `{ data }` envelope; the page mocks THIS module in tests.
//
// Weekday convention: the API uses 0=Sun..6=Sat; the design-system
// WeeklyScheduleEditor uses 0=Mon..6=Sun. The page maps between them.

import { apiClient } from '../../api/client.js'

export interface Doctor {
  id: string
  name: string
  specialty: string
  location: string | null
  status: 'active' | 'inactive'
}

export interface NewDoctor {
  name: string
  specialty: string
  location?: string
}

export interface DoctorPatch {
  name?: string
  specialty?: string
  location?: string | null
  status?: 'active' | 'inactive'
}

export interface ScheduleBlock {
  id: string
  weekday: number // 0=Sun..6=Sat
  startTime: string // 'HH:MM'
  endTime: string
  slotMinutes: number
}

export interface TimeOff {
  id: string
  startsAt: string
  endsAt: string
  reason: string | null
}

export interface DoctorSchedule {
  schedules: ScheduleBlock[]
  timeOff: TimeOff[]
}

export interface WeeklyBlockInput {
  weekday: number
  startTime: string
  endTime: string
  slotMinutes?: number
}

export interface Cita {
  id: string
  patientName: string
  patientPhone: string | null
  doctorName: string
  specialty: string
  startsAt: string
  endsAt: string
  status: string
  reason: string | null
  firstVisit: boolean | null
  coverage: string | null
  insurer: string | null
}

/** GET /providers — the tenant's doctors (session-scoped, unwraps `{ data: { providers } }`). */
export async function getDoctores(): Promise<Doctor[]> {
  const res = await apiClient.get<{ data: { providers: Doctor[] } }>('/providers')
  return res.data.data.providers
}

/** POST /providers — registers a doctor; returns the created row. */
export async function createDoctor(input: NewDoctor): Promise<Doctor> {
  const res = await apiClient.post<{ data: Doctor }>('/providers', input)
  return res.data.data
}

/** PATCH /providers/:id — partial edit incl. activating/deactivating the doctor. */
export async function updateDoctor(id: string, patch: DoctorPatch): Promise<Doctor> {
  const res = await apiClient.patch<{ data: Doctor }>(`/providers/${id}`, patch)
  return res.data.data
}

/** GET /providers/:id/schedule — weekly blocks + time-off. Weekday is API convention (0=Sun..6=Sat). */
export async function getDoctorSchedule(id: string): Promise<DoctorSchedule> {
  const res = await apiClient.get<{ data: DoctorSchedule }>(`/providers/${id}/schedule`)
  return res.data.data
}

/** PUT /providers/:id/schedule — replaces the doctor's whole weekly block set. */
export async function saveDoctorSchedule(id: string, blocks: WeeklyBlockInput[]): Promise<void> {
  await apiClient.put(`/providers/${id}/schedule`, { blocks })
}

/** POST /providers/:id/time-off — blocks a date range from booking. */
export async function addTimeOff(
  id: string,
  input: { startsAt: string; endsAt: string; reason?: string },
): Promise<TimeOff> {
  const res = await apiClient.post<{ data: TimeOff }>(`/providers/${id}/time-off`, input)
  return res.data.data
}

/** DELETE /providers/:id/time-off/:timeOffId — reopens the blocked range. */
export async function deleteTimeOff(id: string, timeOffId: string): Promise<void> {
  await apiClient.delete(`/providers/${id}/time-off/${timeOffId}`)
}

/** GET /agenda — the tenant's citas across doctors, for the Citas list. */
export async function getCitas(): Promise<Cita[]> {
  const res = await apiClient.get<{ data: { appointments: Cita[] } }>('/agenda')
  return res.data.data.appointments
}

/** Confirm a held (pending) cita — the patient is notified on WhatsApp. */
export async function confirmCita(id: string): Promise<void> {
  await apiClient.post(`/agenda/${id}/confirm`)
}

/** Cancel a cita — frees the reminder and notifies the patient. */
export async function cancelCita(id: string): Promise<void> {
  await apiClient.post(`/agenda/${id}/cancel`)
}

/** Mark that the patient never arrived — internal only (no patient message),
 *  notifies the inbox staff so they can follow up. */
export async function noShowCita(id: string): Promise<void> {
  await apiClient.post(`/agenda/${id}/no-show`)
}
